"use client";

import {
  focusManager,
  onlineManager,
  useQueryClient,
} from "@tanstack/react-query";
import { type ReactNode, useEffect } from "react";
import { useAuthState } from "@/hooks/use-auth-state";
import { useRealtimeReconnect, useUserRealtime } from "@/hooks/use-realtime";
import { invalidateBuddyRelationships } from "@/lib/buddy/relationship-cache";
import { realtimeClient } from "@/lib/realtime/client";

/**
 * Owns the single app-wide realtime socket. Connection is gated on two signals:
 *  - auth: only while signed in (the server would 401 otherwise);
 *  - foreground: paused on background/hidden (the OS kills the socket anyway)
 *    and resumed on return, which triggers receipt-aware catchup in subscribed hooks;
 *  - network: reconnect after connectivity returns.
 *
 * Background delivery stays on native push (FCM/APNs) — the socket is for the
 * foreground live experience only. Reuses TanStack Query's `focusManager`,
 * which `setupAppLifecycle` already wires to native resume + web visibility.
 */
export function RealtimeProvider({ children }: { children: ReactNode }) {
  const { authStatus } = useAuthState();
  const queryClient = useQueryClient();
  useUserRealtime((event) => {
    if (event.type === "buddy.status") {
      invalidateBuddyRelationships(queryClient, event.targetUserId);
    }
  });
  useRealtimeReconnect(() => {
    if (authStatus === "authenticated")
      invalidateBuddyRelationships(queryClient);
  });

  useEffect(() => {
    // "unknown" is not "signed out".
    //
    // `useAuthState` reports `unknown` while the identity query is pending OR
    // when it errored, and the query refetches on window focus and on network
    // reconnect. Collapsing that to a boolean meant every transient failure —
    // a slow session read, a blip on a train — closed a perfectly good socket,
    // which then reconnected a moment later once the query recovered.
    //
    // The cost was measurable: 3,015 connects against 728 disconnects over two
    // weeks. `realtime_disconnected` only fires on a genuine drop, so roughly
    // three quarters of all reconnects were the client hanging up on itself.
    //
    // Only a definitive answer should move the socket. While the answer is
    // unknown, leave it exactly as it is.
    if (authStatus === "unknown") return;
    realtimeClient.setEnabled(authStatus === "authenticated");
  }, [authStatus]);

  useEffect(() => {
    if (focusManager.isFocused()) {
      realtimeClient.resume();
    } else {
      realtimeClient.pause();
    }
    return focusManager.subscribe((focused) => {
      if (focused) realtimeClient.resume();
      else realtimeClient.pause();
    });
  }, []);

  useEffect(() => {
    realtimeClient.setOnline(onlineManager.isOnline());
    return onlineManager.subscribe((online) =>
      realtimeClient.setOnline(online),
    );
  }, []);

  return <>{children}</>;
}
